import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionSummary, Box, Stack, Typography } from '@mui/material';
import { dynamicInvoiceProductLineItemSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { QuestionMarkCircle, WarningFilled } from '@vy/ui-design-system';
import { formatPrice } from '@vy/services';
import { AGGREGATION_OPERATORS } from '@vy/types';
import { formatNumber, isNotNullOrUndefined, } from '@vy/utils';
import { FC } from 'react';
import { Status } from '../../../../../components/design/statuses/Status/Status';
import { VyLinearProgress } from '../../../../../components/new-design/data-display/VyLinearProgress/VyLinearProgress';
import { VyTooltip } from '../../../../../components/new-design/VyTooltip';
import { useExchangeRates } from '../../../../../hooks/requests/exchange-rates/use-exchange-rates';
import { useTreatLastAsDaily } from '../../../../../hooks/use-treat-last-as-daily';
import { formatCommitmentAmount } from '../../../../../services/commitment/formatCommitmentAmount';
import { getCommitmentLabel } from '../../../../../services/commitment/getCommitmentLabel';
import type { ModifyLineItemData, UsageItemSummaryProps } from '../types';
import { getLineItemPercentage } from '../utils/get-line-item-percentage';
import { LineItemHeader } from './LineItemHeader';
import { MetricBox } from './MetricBox';
import { ModifyUsageLineItemSection } from './ModifyUsageLineItemSection';
import { COLORS, lineItemActionStyles, usageItemSummaryStyles } from './styles';

export const UsageItemSummary: FC<UsageItemSummaryProps> = ({
  lineItem,
  invoiceId,
  customerId,
  reviewStatus,
  currency,
  aggregationOperator,
}) => {
  const treatLastAsDaily = useTreatLastAsDaily();
  const unitsLabel = aggregationOperator === AGGREGATION_OPERATORS.Last && treatLastAsDaily ? 'Seat-days' : 'Units';
  const commitment = lineItem.commitment;
  const hasCommitment =
    isNotNullOrUndefined(commitment?.consumption) && isNotNullOrUndefined(commitment?.amount);
  const isOverage = hasCommitment && commitment.consumption > commitment.amount;

  const shouldShowMenu = reviewStatus !== 'Approved';
  const isDisabled = lineItem.disabled;
  const percentage = getLineItemPercentage(lineItem);
  const { data: exchangeRates } = useExchangeRates();

  const lineItemData: ModifyLineItemData = {
    type: 'Usage',
    name: lineItem.name,
    description: lineItem.description,
    price: lineItem.totalAmount,
    modifiedName: null,
    modifiedDescription: null,
    modifiedPrice: null,
  };

  return (
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        ...usageItemSummaryStyles.accordionSummary,
        opacity: isDisabled ? 0.5 : 1,
      }}
    >
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <LineItemHeader
          name={lineItem.name}
          description={lineItem.description}
          percentage={percentage}
        />
        {hasCommitment && (
          <Stack
            direction="column"
            spacing={1.5}
            {...getSelectorAttribute(dynamicInvoiceProductLineItemSelectors.commitmentUsage)}
            sx={{
              alignItems: "center"
            }}>
            <Box sx={usageItemSummaryStyles.progressContainer}>
              <VyLinearProgress
                currentValue={commitment.consumption}
                totalValue={commitment.amount}
                progressColor={COLORS.progress}
                renderLabel={() => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography variant="body2" sx={usageItemSummaryStyles.progressText}>
                      <strong>{formatNumber(commitment.consumption)}</strong> /{' '}
                      {formatNumber(commitment.amount)} (
                      {((commitment.consumption / commitment.amount) * 100).toFixed(2)}%)
                    </Typography>
                    <VyTooltip
                      placement="top"
                      tooltipContent={
                        <>
                          You've used {formatNumber(commitment.consumption)} units out of the {formatNumber(commitment.amount)}
                          <br />
                          committed units for this billing period
                        </>
                      }
                    >
                      <Box component="span" sx={{ display: 'flex', cursor: 'pointer' }}>
                        <QuestionMarkCircle />
                      </Box>
                    </VyTooltip>
                  </Box>
                )}
              />
            </Box>
          </Stack>
        )}
        {isOverage && (
          <Status status="ERROR" style={usageItemSummaryStyles.overageStatus} {...getSelectorAttribute(dynamicInvoiceProductLineItemSelectors.chips.overage)}>
            <WarningFilled style={usageItemSummaryStyles.overageIcon} />
            Overage
          </Status>
        )}
      </Stack>
      <Stack
        direction="row"
        sx={[{
          alignItems: "center"
        }, lineItemActionStyles.actionsContainer]}>
        <Stack
          direction="row"
          sx={[{
            alignItems: "center"
          }, lineItemActionStyles.metricsContainer]}>
          {isNotNullOrUndefined(lineItem.basePrice) && (
            <MetricBox label="Base amount" value={formatPrice(lineItem.basePrice, currency)} />
          )}
          {hasCommitment && (
            <MetricBox
              label={getCommitmentLabel(commitment.type)}
              value={
                formatCommitmentAmount(commitment.type, commitment.amount, currency, exchangeRates)}
              selector={dynamicInvoiceProductLineItemSelectors.commitment}
            />
          )}
          <MetricBox label={unitsLabel} value={formatNumber(lineItem.totalUnits)} selector={dynamicInvoiceProductLineItemSelectors.quantity} />
          <MetricBox label="Amount" value={formatPrice(lineItem.totalAmount, currency)} selector={dynamicInvoiceProductLineItemSelectors.amount} />
        </Stack>
        {shouldShowMenu && (
          <ModifyUsageLineItemSection
            lineItemId={lineItem.lineItemId}
            customerId={customerId}
            invoiceId={invoiceId}
            lineItemData={lineItemData}
            disabled={isDisabled}
          />
        )}
      </Stack>
    </AccordionSummary>
  );
};
